import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

const WishlistContext = createContext();

export const useWishlist = () => {
  const context = useContext(WishlistContext);
  if (!context) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
};

export const WishlistProvider = ({ children }) => {
  const [wishlistItems, setWishlistItems] = useState([]);

  // Load wishlist from localStorage on mount
  useEffect(() => {
    const loadWishlist = () => {
      const stored = localStorage.getItem('wishlist');
      if (stored) {
        try {
          const items = JSON.parse(stored);
          setWishlistItems(items);
        } catch (error) {
          console.error('Error loading wishlist:', error);
          localStorage.removeItem('wishlist');
          setWishlistItems([]);
        }
      } else {
        setWishlistItems([]);
      }
    };

    loadWishlist();

    // Listen for storage events to sync when localStorage is cleared externally
    const handleStorageChange = (e) => {
      if (e.key === 'wishlist') {
        loadWishlist();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also listen for custom events (for same-window updates)
    const handleCustomStorageChange = () => {
      loadWishlist();
    };
    window.addEventListener('wishlistStorageChanged', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('wishlistStorageChanged', handleCustomStorageChange);
    };
  }, []);

  // Save wishlist to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('wishlist', JSON.stringify(wishlistItems));
  }, [wishlistItems]);

  // Check if product is in wishlist
  const isInWishlist = useCallback((productId, variantId) => {
    return wishlistItems.some(
      (item) => item.productId === productId && item.variantId === variantId
    );
  }, [wishlistItems]);

  // Add to wishlist (max 20 items - oldest item removed when limit reached)
  const addToWishlist = useCallback((product) => {
    const { productId, variantId } = product;
    
    // Check if already in wishlist
    if (isInWishlist(productId, variantId)) {
      toast.info('Product is already in your wishlist');
      return;
    }

    const newItem = {
      productId,
      variantId, // Internal variant ID (for cart/backend operations)
      variantPublicId: product.variantPublicId || product.variantId || variantId, // Variant's publicId for URL (used in /product/{publicId}-{variantPublicId})
      publicId: product.publicId,
      name: product.name || product.label,
      label: product.label || product.name,
      images: product.images || [],
      sellingPrice: product.sellingPrice || 0,
      mrp: product.mrp || product.sellingPrice || 0,
      category: product.category,
      addedAt: new Date().toISOString(),
    };

    setWishlistItems((prev) => {
      const MAX_WISHLIST_ITEMS = 20;
      // If we have 20 or more items, remove the oldest (last) item
      // New items are added to the beginning, so oldest is at the end
      if (prev.length >= MAX_WISHLIST_ITEMS) {
        // Keep only the first 19 items and add new item at the beginning
        return [newItem, ...prev.slice(0, MAX_WISHLIST_ITEMS - 1)];
      }
      // Otherwise, just add the new item
      return [newItem, ...prev];
    });
    
    toast.success('Added to wishlist!');
  }, [isInWishlist]);

  // Remove from wishlist
  const removeFromWishlist = useCallback((productId, variantId) => {
    setWishlistItems((prev) =>
      prev.filter(
        (item) => !(item.productId === productId && item.variantId === variantId)
      )
    );
    toast.success('Removed from wishlist');
  }, []);

  // Toggle wishlist (add if not present, remove if present)
  const toggleWishlist = useCallback((product) => {
    const { productId, variantId } = product;
    
    if (isInWishlist(productId, variantId)) {
      removeFromWishlist(productId, variantId);
    } else {
      addToWishlist(product);
    }
  }, [isInWishlist, addToWishlist, removeFromWishlist]);

  // Clear entire wishlist
  const clearWishlist = useCallback(() => {
    setWishlistItems([]);
    toast.success('Wishlist cleared');
  }, []);

  // Get wishlist count
  const wishlistCount = wishlistItems.length;

  const value = {
    wishlistItems,
    wishlistCount,
    isInWishlist,
    addToWishlist,
    removeFromWishlist,
    toggleWishlist,
    clearWishlist,
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
};

